import React, { useState, useRef, useMemo } from "react";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_STRING } from "typeConstants/dataTypes";
import { CustomerRCTAccountSettingsDto } from "interfaces/Mueller.Business.Models";
import { CalcToolCodes } from "typeConstants/calcTypeCodes";

interface CustomerRCTAccountSettingsFormProps {
  customerNumber: number;
  settings: CustomerRCTAccountSettingsDto;
  onSave?: (settings: CustomerRCTAccountSettingsDto) => void;
  onNext?: (settings: CustomerRCTAccountSettingsDto) => void;
  onCancel: () => void;
  canEdit: boolean;
  portalTargetClass: string;
}

const newSettings: CustomerRCTAccountSettingsDto = {
  customerNumber: null,
  calcToolCode: "",
  calcToolName: "",
  licenseCode: "",
  userName: "",
  password: "",
  domain: "",
  webserviceURL: "",
  wcfEndpointName: "",
  referenceWebserviceURL: "",
  referenceWCFEndpointName: "",
  configWebserviceURL: "",
  configWCFEndpointName: "",
  webserviceVersion: "",
};

const CustomerRCTAccountSettingsForm = (props: CustomerRCTAccountSettingsFormProps) => {
  const [editSettings, setEditSettings] = useState(props.settings ?? newSettings);
  const formControlMethods = useRef({});

  // MGrid form

  const onSettingsChange = (updatedObject) => setEditSettings({ ...updatedObject });
  const memoizedFormDefinition = useMemo(() => {
    // There are more calc codes, but only these 4 are in use in MuellerHomeowner.dbo.CalcToolAccounts
    const calcToolOptions = [
      { label: "RCT", value: CalcToolCodes.Rct },
      { label: "ISO HomeValue", value: CalcToolCodes.Iso },
      { label: "RCT ExpressLync", value: CalcToolCodes.RctExpressLync },
      { label: "RCT FIT", value: CalcToolCodes.RctFit },
    ];
    const enabled = () => (props.canEdit ? true : false);
    const formDef = [
      [
        {
          dataType: DATA_TYPE_DROPDOWN2,
          label: "Calc Tool",
          source: "calcToolCode",
          enabled: enabled,
          dataTypeOptions: { options: calcToolOptions },
          hasValidationError: validator.required,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "License Code",
          source: "licenseCode",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Username",
          source: "userName",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Password",
          source: "password",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Domain",
          source: "domain",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Webservice URL",
          source: "webserviceURL",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "WCF Endpoint Name",
          source: "wcfEndpointName",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Reference Webservice URL",
          source: "referenceWebserviceURL",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Reference WCF Endpoint Name",
          source: "referenceWCFEndpointName",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Config Webservice URL",
          source: "configWebserviceURL",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Config WCF Endpoint Name",
          source: "configWCFEndpointName",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
      [
        {
          dataType: DATA_TYPE_STRING,
          label: "Webservice Version",
          source: "webserviceVersion",
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: null,
        },
      ],
    ];
    return formDef;
  }, [props.canEdit]);

  // Buttons

  const wrappedOnSave = () => {
    //@ts-ignore
    if (formControlMethods.current.isFormValid() && props.onSave) {
      if (!editSettings.customerNumber) {
        // Set this in case we're adding new settings
        editSettings.customerNumber = props.customerNumber;
      }
      props.onSave(editSettings);
    } else {
      props.onNext(editSettings);
    }
  };

  const saveButtonLabel = props.onNext ? "Next" : "Save";
  const cancelButtonLabel = props.canEdit ? "Cancel" : "Close";

  return (
    <>
      <MGridForm
        data={editSettings}
        onChange={onSettingsChange}
        formDefinition={memoizedFormDefinition}
        portalTarget={() => document.querySelector(props.portalTargetClass)}
        getControlMethods={(cm) => {
          formControlMethods.current = cm;
        }}
      />
      <div className="button-row-margin">
        <Button onClick={props.onCancel}>{cancelButtonLabel}</Button>
        {props.canEdit && <Button onClick={wrappedOnSave}>{saveButtonLabel}</Button>}
      </div>
    </>
  );
};

export default CustomerRCTAccountSettingsForm;
